import {
  useCandidates,
  useJob,
  useReleaseRecommendation,
  useUpdateCandidate,
  useUsers,
} from "@vidario/api-client";
import {
  CANDIDATE_NOUN,
  countLabel,
  formatCount,
  isRecommendationReleased,
  releaseCheck,
  type Candidate,
} from "@vidario/domain";
import { Button, Card, EmptyState, cn, useToast } from "@vidario/ui";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CheckCircle, Download, Sparkles, Users } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { useNavigate } from "react-router";

import { useRole } from "../../guards/useRole";
import { RecommendationCard } from "./RecommendationCard";
import { ReleaseRecommendationDialog } from "./ReleaseRecommendationDialog";

import { ANONYM_OUTLINE } from "../../anonym/outline";
import { useAnonymView } from "../../anonym/useAnonymView";

const SOFT_LIMIT = 5;
const HARD_LIMIT = 10;

export interface RecommendationPanelProps {
  jobId: string;
}

/** Body of the Empfehlungen view — reused by the standalone
 * /recommendation route and the third tab on the Longlist page.
 * Renders the admin action buttons, soft/hard limit warnings, insights
 * banner, and the drag-reorderable candidate list. Assumes the parent
 * supplies a page heading. */
const EMPTY_STATE_ID = "recommendation-empty";

export function RecommendationPanel({ jobId }: RecommendationPanelProps): ReactElement {
  const navigate = useNavigate();
  const role = useRole();
  const isAdmin = role === "admin";
  const toast = useToast();

  const { data: job } = useJob(jobId);
  const { data: candidates = [] } = useCandidates(jobId);
  const update = useUpdateCandidate();
  const release = useReleaseRecommendation();
  const { data: users = [] } = useUsers();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const anonym = useAnonymView();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const [localOrder, setLocalOrder] = useState<string[] | null>(null);
  // Where focus goes once a card leaves the list (#162): the next card, else the
  // previous one, else the empty state.
  const focusAfterMove = useRef<{ moved: string; target: string } | null>(null);

  const recommended: Candidate[] = useMemo(() => {
    const list = candidates.filter((c) => c.status === "recommendation");
    const sorted = [...list].sort((a, b) => {
      const ra = a.recommendationRank ?? Number.MAX_SAFE_INTEGER;
      const rb = b.recommendationRank ?? Number.MAX_SAFE_INTEGER;
      if (ra !== rb) return ra - rb;
      return (b.score ?? 0) - (a.score ?? 0);
    });
    if (!localOrder) return sorted;
    const byId = new Map(sorted.map((c) => [c.id, c]));
    // Render the drag order first, dropping ids that have left the
    // recommendation list…
    const ordered = localOrder.flatMap((id) => {
      const c = byId.get(id);
      return c ? [c] : [];
    });
    // …then append any candidate that entered `recommendation` while the
    // panel was mounted and so isn't in localOrder yet. Without this, a
    // candidate promoted mid-session is silently omitted from the list.
    const seen = new Set(localOrder);
    const appended = sorted.filter((c) => !seen.has(c.id));
    return [...ordered, ...appended];
  }, [candidates, localOrder]);

  const editable = isAdmin && job?.status !== "placed" && job?.status !== "closed";

  // Once the moved card has left the list (and only then), focus its neighbour.
  useEffect(() => {
    const pending = focusAfterMove.current;
    if (!pending || recommended.some((c) => c.id === pending.moved)) return;
    focusAfterMove.current = null;
    document.getElementById(pending.target)?.focus();
  }, [recommended]);

  const handleDragEnd = (evt: DragEndEvent): void => {
    const { active, over } = evt;
    if (!over || active.id === over.id) return;
    const ids = recommended.map((c) => c.id);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(ids, oldIndex, newIndex);
    setLocalOrder(next);
    next.forEach((id, i) => {
      const c = recommended.find((x) => x.id === id);
      if (!c) return;
      const newRank = i + 1;
      if (c.recommendationRank === newRank) return;
      update.mutate({ jobId, candidateId: id, patch: { recommendationRank: newRank } });
    });
  };

  const overHardLimit = recommended.length > HARD_LIMIT;
  const overSoftLimit = recommended.length > SOFT_LIMIT;

  // `job` is loaded by the time the release UI renders (early return below).
  const check = releaseCheck(recommended, job?.status ?? "draft");
  const owner = users.find((u) => u.id === job?.createdBy);

  const handleRelease = (): void => {
    release.mutate(jobId, {
      onSuccess: () => {
        setConfirmOpen(false);
        toast.push({
          kind: "success",
          title: "Empfehlung freigegeben",
          message: "Der Recruiter wurde per E-Mail benachrichtigt.",
        });
      },
      onError: () => {
        toast.push({
          kind: "error",
          title: "Freigabe fehlgeschlagen",
          message: "Bitte versuche es erneut.",
        });
      },
    });
  };

  if (!job) {
    return (
      <p className="text-sm text-text-secondary" role="status">
        Lade Empfehlung…
      </p>
    );
  }

  const ids = recommended.map((c) => c.id);
  const released = isRecommendationReleased(job);
  const bookingBaseUrl = import.meta.env.VITE_BOOKING_BASE_URL as string | undefined;

  return (
    <div className="flex flex-col">
      <div className="mb-5 flex flex-wrap items-center justify-end gap-2.5">
        <div className="mr-auto text-sm text-text-secondary">
          {countLabel(recommended.length, CANDIDATE_NOUN)} in der Empfehlung
        </div>
        {/* Only an Outreaching job can be released; a closed one shows neither state. */}
        {isAdmin && job.status === "outreaching" && (
          <Button
            leftIcon={<CheckCircle size={16} />}
            onClick={() => setConfirmOpen(true)}
            disabled={!check.canRelease || overHardLimit}
            {...(check.blockedReason ? { title: check.blockedReason } : {})}
          >
            Auf Empfehlung freigeben
          </Button>
        )}
        {isAdmin && released && (
          <span className="inline-flex h-10 items-center gap-2 rounded-md bg-success-50 px-3.5 text-[13px] font-medium text-success-700">
            <CheckCircle size={14} /> Freigegeben
          </span>
        )}
        <Button
          variant="primary"
          leftIcon={<Download size={16} />}
          onClick={() => void navigate(`/jobs/${jobId}/recommendation/pdf`)}
          disabled={recommended.length === 0}
        >
          PDF-Export
        </Button>
      </div>

      {overHardLimit && (
        <Card className="mb-5 border-[#FCA5A5] bg-danger-50 text-sm text-danger-700" role="alert">
          <b>Maximal 10 Kandidaten pro Empfehlung.</b> Bitte entferne{" "}
          {recommended.length - HARD_LIMIT}, bevor du freigibst.
        </Card>
      )}
      {!overHardLimit && overSoftLimit && (
        <Card
          className="mb-5 border-warning-200 bg-warning-50 text-sm text-warning-700"
          role="status"
        >
          Mehr als {SOFT_LIMIT} Empfehlungen schwächen den Pitch — wirklich alle aufnehmen?
        </Card>
      )}

      <Card className="mb-6 border-0 px-7 py-5 text-neutral-0 bg-[linear-gradient(135deg,var(--color-primary-800)_0%,var(--color-primary-600)_100%)]">
        <div className="flex flex-wrap items-start gap-6">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider">
            <Sparkles size={12} /> Vidario Insights
          </div>
          <div className="min-w-0 flex-1 basis-[300px] text-sm leading-[1.7]">
            {formatCount(recommended.length)} von {formatCount(candidates.length)} gesichteten
            Kandidaten {recommended.length === 1 ? "ist" : "sind"} in die finale Empfehlung gelangt.
            Die Top-Picks erfüllen jeweils die Must-Haves und unterscheiden sich in Domänen-Tiefe,
            Seniorität und Standort.
          </div>
        </div>
      </Card>

      {recommended.length === 0 ? (
        <div id={EMPTY_STATE_ID} tabIndex={-1} className="focus:outline-none">
          <EmptyState
            icon={<Users size={28} />}
            title="Noch keine Empfehlung"
            description={
              isAdmin
                ? "Öffne einen Kandidaten und klicke „In Empfehlung aufnehmen“."
                : "Das Vidario-Team bereitet die Empfehlung vor."
            }
          />
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <div
              className={cn("flex flex-col gap-5", anonym && ANONYM_OUTLINE)}
              data-testid="recommendation-list"
            >
              {recommended.map((c, i) => (
                <RecommendationCard
                  key={c.id}
                  jobId={jobId}
                  candidate={c}
                  job={job}
                  rank={i + 1}
                  isTop={i === 0}
                  editable={editable}
                  bookingBaseUrl={bookingBaseUrl}
                  onOpen={() => void navigate(`/jobs/${jobId}/candidates/${c.id}`)}
                  onMovedBack={() => {
                    const neighbour = recommended[i + 1] ?? recommended[i - 1];
                    focusAfterMove.current = {
                      moved: c.id,
                      target: neighbour ? `recommendation-name-${neighbour.id}` : EMPTY_STATE_ID,
                    };
                  }}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {isAdmin && (
        <ReleaseRecommendationDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          onConfirm={handleRelease}
          loading={release.isPending}
          recruiter={owner ? { name: owner.name, email: owner.email } : undefined}
          missingText={check.missingText}
        />
      )}
    </div>
  );
}
